import { Check } from 'lucide-react';
import { READER_AVATAR_OPTIONS, type ReaderAvatarSlug } from '@/constants/reader-avatars';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { cn } from '@/lib/cn';

export interface ReaderAvatarPickerProps {
  value: ReaderAvatarSlug;
  onChange: (value: ReaderAvatarSlug) => void;
  name: string;
  disabled?: boolean;
}

export function ReaderAvatarPicker({ value, onChange, name, disabled }: ReaderAvatarPickerProps) {
  return (
    <div>
      <div className="mb-2 text-[12px] font-semibold text-or-slate-700">Choose an avatar</div>
      {/* Wrap on phones (fixed 56px avatars overflow a forced 5-up row on a
          narrow card and overlap); keep the tidy 5-column grid from `sm` up. */}
      <div
        className="flex flex-wrap gap-2 sm:grid sm:grid-cols-5"
        role="radiogroup"
        aria-label="Reader avatar"
      >
        {READER_AVATAR_OPTIONS.map((option) => {
          const selected = option.slug === value;
          return (
            <button
              key={option.slug}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              disabled={disabled}
              onClick={() => onChange(option.slug)}
              className={cn(
                'relative rounded-2xl border bg-white p-1.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40 disabled:cursor-not-allowed disabled:opacity-60',
                selected
                  ? 'border-or-orange-400 ring-2 ring-or-orange-300/40'
                  : 'border-or-slate-200 hover:border-or-orange-200 hover:bg-or-orange-50',
              )}
            >
              <ReaderAvatar name={name || option.label} avatarSlug={option.slug} size="lg" />
              {selected ? (
                <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-or-orange-500 text-white">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
